import { Send } from 'lucide-react';
import { useState } from 'react';
import { toApiError, type MessageItem } from '@/shared/api';
import { problemText } from '@/shared/api/problemText';
import { useOnline } from '@/shared/hooks/useOnline';
import { cn } from '@/shared/lib/cn';
import { channelLabel } from '../inboxModel';

type Outcome = { tone: 'error' | 'info'; text: string } | null;

// Ein Gateway kann antworten, nachdem der Server die Nachricht schon angenommen hat.
const NO_ANSWER = new Set([0, 502, 503, 504]);

/**
 * Schreibfeld. Der Server nimmt die Nachricht an und stellt sie über die Versand-Warteschlange zu;
 * ob der Kanal sie annimmt, zeigt danach die Nachricht im Verlauf. Am Feld steht nur, was hier
 * passiert: abgelehnt bleibt der Text mit Grund stehen; bleibt die Antwort aus, ist offen, ob die
 * Nachricht angenommen wurde — der Text bleibt, und erst der Verlauf zeigt es (kein Doppelversand).
 */
export function ConversationComposer({
  name,
  channel,
  closed,
  readOnly,
  onSend,
}: {
  name: string;
  channel: string | null;
  closed: boolean;
  readOnly?: boolean;
  onSend: (text: string) => Promise<MessageItem>;
}) {
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome>(null);
  const online = useOnline();
  const locked = Boolean(readOnly) || closed || !online;

  async function submit() {
    const text = reply.trim();
    if (!text || locked || sending) return;
    setSending(true);
    setOutcome(null);
    try {
      await onSend(text);
      setReply('');
    } catch (error) {
      const problem = await toApiError(error);
      setOutcome({
        tone: 'error',
        text: NO_ANSWER.has(problem.status)
          ? 'Keine Antwort vom Server. Ob die Nachricht angenommen wurde, zeigt der Verlauf, sobald er neu geladen ist – erst dann erneut senden.'
          : problemText(problem, 'Nicht gesendet.'),
      });
    } finally {
      setSending(false);
    }
  }

  const via = channelLabel(channel);
  return (
    <div className="flex-none px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 sm:px-5">
      <div
        className={cn(
          'rounded-[18px] border-[1.5px] bg-white',
          locked ? 'border-line' : 'border-forest',
        )}
      >
        <label
          htmlFor="inbox-reply"
          className="text-ink-soft block px-3.5 pt-2.5 text-xs font-bold"
        >
          Antwort an {name} · geht über {via}
        </label>
        <textarea
          id="inbox-reply"
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void submit();
          }}
          disabled={locked}
          placeholder={closed ? 'Geschlossen – keine Antwort möglich' : `Antwort an ${name} …`}
          rows={2}
          className="text-ink w-full resize-none border-0 bg-transparent px-3.5 py-1.5 text-[15px] leading-relaxed outline-none disabled:cursor-not-allowed"
        />
        <div className="border-card-alt flex items-center gap-3 border-t py-2 pl-3.5 pr-2">
          <span className="text-ink-soft min-w-0 flex-1 text-xs">
            {!online
              ? 'Keine Verbindung – Senden geht wieder, sobald sie zurück ist.'
              : readOnly
                ? 'Vorschau – Senden ist aus.'
                : closed
                  ? 'Schreibt der Gast erneut, öffnet sich die Unterhaltung von selbst.'
                  : 'Strg + Enter sendet · nur Text'}
          </span>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={sending || !reply.trim() || locked}
            className="bg-forest rounded-pill inline-flex min-h-11 flex-none items-center gap-1.5 px-5 text-sm font-bold text-white disabled:opacity-40"
          >
            {sending ? 'Wird gesendet …' : 'Senden'}
            <Send size={14} aria-hidden />
          </button>
        </div>
      </div>
      {outcome && (
        <p
          role={outcome.tone === 'error' ? 'alert' : 'status'}
          className={cn(
            'mt-2 text-[13px]',
            outcome.tone === 'error' ? 'text-sealwax' : 'text-ink-soft',
          )}
        >
          {outcome.text}
        </p>
      )}
    </div>
  );
}
